import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, setToken } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const u = localStorage.getItem("tc_user");
    return u ? JSON.parse(u) : null;
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Always try /auth/me on mount. Works via Bearer (sessionStorage) or
    // httpOnly cookie — whichever is available.
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        localStorage.setItem("tc_user", JSON.stringify(r.data));
      })
      .catch(() => {
        localStorage.removeItem("tc_user");
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (identifier, password) => {
    const r = await api.post("/auth/login", { identifier, password });
    setToken(r.data.access_token);
    localStorage.setItem("tc_user", JSON.stringify(r.data.user));
    setUser(r.data.user);
    return r.data.user;
  }, []);

  // Called after PIN / dancer flows — token comes from the API response.
  const setSession = useCallback((token, userObj) => {
    if (token) setToken(token);
    localStorage.setItem("tc_user", JSON.stringify(userObj));
    setUser(userObj);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.warn("Logout request failed (continuing with local cleanup):", err);
    }
    setToken(null);
    localStorage.removeItem("tc_user");
    setUser(null);
    window.location.href = "/login";
  }, []);

  const value = useMemo(
    () => ({ user, login, logout, loading, setSession }),
    [user, loading, login, logout, setSession],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
